import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { authedFetch, hasUserAuth, useAuth, useTenant } from "@/lib/auth";
import { features } from "@/lib/features";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ResourceUsage } from "./usage-meter";
import { UsageMeter } from "./usage-meter";

const PLAN_DISPLAY: Record<string, { label: string; tagline: string }> = {
  free: {
    label: "Public Beta",
    tagline: "All Pro team capabilities unlocked during public beta",
  },
  pro: {
    label: "Public Beta (Pro)",
    tagline: "All Pro team capabilities unlocked during public beta",
  },
  enterprise: {
    label: "Enterprise",
    tagline: "Custom limits and dedicated support",
  },
};

function overLimitHint(usage: ResourceUsage): string | undefined {
  if (usage.unlimited || usage.used <= usage.limit) return undefined;
  return `${usage.used - usage.limit} over limit — upgrade to add more`;
}

function formatStorageValue(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}

export function BillingSection({ hideTitle = false }: { hideTitle?: boolean }) {
  const { auth } = useAuth();
  const tenantId = useTenant();
  const utils = trpc.useUtils();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingEmail, setBillingEmail] = useState<string>("");
  const [billingEmailDirty, setBillingEmailDirty] = useState(false);
  const [pollExpired, setPollExpired] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (searchParams.get("checkout") !== "success") {
      setPollExpired(false);
      return;
    }
    pollTimerRef.current = setTimeout(() => setPollExpired(true), 10_000);
    return () => clearTimeout(pollTimerRef.current);
  }, [searchParams]);

  const { data: workspaces } = trpc.tenant.listForUser.useQuery();
  const currentWorkspace =
    workspaces?.find((w) => w.isCurrent) ??
    workspaces?.find((w) => w.id === tenantId);
  const isOwner = currentWorkspace?.role === "owner";

  const { data: tenantData } = trpc.tenant.getById.useQuery(
    { id: tenantId },
    { enabled: !!tenantId },
  );

  useEffect(() => {
    if (tenantData?.billingEmail) setBillingEmail(tenantData.billingEmail);
  }, [tenantData?.billingEmail]);

  const updateTenantMutation = trpc.tenant.update.useMutation({
    onSuccess: () => {
      toast.success("Billing email updated");
      utils.tenant.getById.invalidate({ id: tenantId });
      setBillingEmailDirty(false);
    },
    onError: (err) => toastTrpcError(err, "Failed to update billing email"),
  });

  const {
    data: planInfo,
    isLoading,
    error: planError,
    refetch: refetchPlan,
  } = trpc.account.planInfo.useQuery(undefined, {
    enabled: hasUserAuth(auth),
    refetchInterval:
      searchParams.get("checkout") === "success" && !pollExpired ? 2000 : false,
  });

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (!checkout) return;
    const removeCheckoutParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("checkout");
          return next;
        },
        { replace: true },
      );
    };
    if (checkout === "success") {
      if (planInfo?.plan === "pro") {
        clearTimeout(pollTimerRef.current);
        toast.success("Welcome to Pro!");
        removeCheckoutParam();
      } else if (pollExpired) {
        toast.info("Your plan is being activated. Check back in a moment.");
        removeCheckoutParam();
      }
    } else if (checkout === "cancelled") {
      toast.info("Checkout cancelled");
      removeCheckoutParam();
    }
  }, [searchParams, planInfo?.plan, setSearchParams, pollExpired]);

  if (!hasUserAuth(auth)) return null;

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-5 w-32 animate-pulse rounded bg-muted" />
        <div className="h-4 w-48 animate-pulse rounded bg-muted" />
        <div className="h-8 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (planError || !planInfo) {
    return (
      <Card className="border-destructive/30">
        <CardContent className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <div className="space-y-1">
            <p className="text-sm font-medium">
              Failed to load plan information
            </p>
            <p className="text-xs text-muted-foreground">
              {planError?.message || "Plan information unavailable"}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchPlan()}
            className="gap-2"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const planDisplay = PLAN_DISPLAY[planInfo.plan] ?? PLAN_DISPLAY.free;
  const isFree = planInfo.plan === "free";
  const isPro = planInfo.plan === "pro";
  const isCheckingOut = searchParams.get("checkout") === "success" && isFree;

  async function handleManageBilling() {
    try {
      const res = await authedFetch("/api/billing/portal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error ?? "Failed to open billing portal");
        return;
      }
      if (data.url) window.location.href = data.url;
    } catch {
      toast.error("Failed to open billing portal");
    }
  }

  return (
    <section className="space-y-4">
      {!hideTitle && (
        <div>
          <h2 className="text-lg font-semibold tracking-tight">
            Plan & billing
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Your current plan and resource usage.
          </p>
        </div>
      )}
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-lg font-semibold tracking-tight">
                {planDisplay.label}
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {planDisplay.tagline}
              </p>
            </div>
            {isOwner ? (
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant="outline"
                  className="rounded-sm border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary-ink"
                >
                  Free in Public Beta
                </Badge>
                {isPro && planInfo.hasSubscription && (
                  <Button variant="outline" onClick={handleManageBilling}>
                    Manage billing
                  </Button>
                )}
              </div>
            ) : (
              <Badge
                variant="outline"
                className="rounded-sm text-xs text-muted-foreground font-normal"
              >
                Contact workspace owner to manage billing
              </Badge>
            )}
          </div>

          {isCheckingOut && (
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Activating your plan...
            </div>
          )}

          <div className="divide-y divide-border">
            <UsageMeter
              label="Walkthroughs"
              sublabel="this month"
              usage={planInfo.usage.walkthroughs}
              overLimitHint={overLimitHint(planInfo.usage.walkthroughs)}
            />
            {features.knowledgeSources && (
              <UsageMeter
                label="Governing authorities"
                sublabel="knowledge sources"
                usage={planInfo.usage.sources}
                overLimitHint={overLimitHint(planInfo.usage.sources)}
              />
            )}
            <UsageMeter
              label="Projects"
              sublabel="total"
              usage={planInfo.usage.projects}
              overLimitHint={overLimitHint(planInfo.usage.projects)}
            />
            {planInfo.usage.storage && (
              <UsageMeter
                label="Artifact storage"
                sublabel="screenshots"
                usage={planInfo.usage.storage}
                formatValue={formatStorageValue}
                overLimitHint={overLimitHint(planInfo.usage.storage)}
              />
            )}
          </div>

          {isOwner && (
            <div className="pt-4 border-t border-border/60">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <Label
                    htmlFor="billing-email"
                    className="text-xs font-medium"
                  >
                    Billing contact email
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Invoices and receipts will be sent here.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id="billing-email"
                    type="email"
                    placeholder="billing@company.com"
                    value={billingEmail}
                    onChange={(e) => {
                      setBillingEmail(e.target.value);
                      setBillingEmailDirty(true);
                    }}
                    className="h-8 min-w-0 flex-1 text-xs sm:w-60 sm:flex-none"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs"
                    disabled={
                      !billingEmailDirty || updateTenantMutation.isPending
                    }
                    onClick={() =>
                      updateTenantMutation.mutate({ billingEmail })
                    }
                  >
                    {updateTenantMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
